import { type Component, createMemo, For, type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import type { CompanyV2 } from "@/domains/company/company/api/types";
import { parseRemotePolicies } from "@/domains/company/company/utils/remote-policy";
import { benefitsView } from "@/domains/company/public-page/utils/benefits-view";
import {
  isRenderableUrl,
  urlLabel,
} from "@/domains/company/public-page/utils/url";
import { AccordionSection } from "./accordion-section";

/**
 * Le tre sezioni narrative della sub-sheet «Informazioni azienda»: Chi siamo,
 * Dettagli, Benefit e cultura.
 *
 * **Ogni sezione possiede il proprio gate**: se non ha dati non renderizza
 * nulla — nessuna intestazione orfana, nessun placeholder, nessun divisore
 * doppio (AC 6). Chi le compone si limita quindi a montarle in ordine, senza
 * ricopiare le condizioni: sono già qui, accanto alle righe che decidono.
 *
 * Le regole di presentazione dei dati company non si riscrivono qui:
 * `parseRemotePolicies` decide quali modalità di lavoro sono renderizzabili,
 * `benefitsView` decide se i benefit sono chip o prosa, `isRenderableUrl` e
 * `urlLabel` decidono cos'è un link. Sono le stesse decisioni della pagina
 * pubblica, ed è il solo modo perché le due superfici non raccontino la stessa
 * azienda in due modi diversi.
 *
 * Lo stesso vale per le stringhe: le nuove vivono sotto
 * `joblist.detailSheet.company.*`, ma le modalità di lavoro passano da
 * `company.publicPage.remotePolicy.*` — la stessa policy non può chiamarsi in
 * due modi in due schermate.
 *
 * **I campi di testo libero si leggono `.trim()`ati**, come fa la vetrina sugli
 * stessi identici campi. Non è difensivismo: una stringa di soli spazi arriva
 * davvero in colonna dalla pagina Impostazioni, perché il suo controllo di
 * "campo vuoto" considera vuota `""` ma non `"   "` e quei campi non hanno un
 * `serialize` che normalizzi in uscita. Con un `Boolean(...)` nudo quel valore
 * apre una sezione con dentro un paragrafo vuoto, o una citazione fatta di due
 * virgolette — l'intestazione orfana che AC 6 vieta ed EC1 chiama il caso più
 * frequente. Il trim va nel gate **e** nel valore renderizzato: sono lo stesso
 * accessor proprio perché non possano divergere.
 */

/**
 * Una riga di Dettagli: etichetta a sinistra, valore a destra.
 *
 * L'etichetta non si comprime e il valore prende ciò che avanza su base zero:
 * `company_size` è testo libero e può essere una riga intera scritta a mano
 * (EC16), che a capo spingerebbe la riga in altezza e sfonderebbe l'allineamento
 * di tutte le altre. Il `min-w-0` qui è la metà indispensabile del troncamento —
 * senza, un figlio flex non scende mai sotto la larghezza del suo contenuto e il
 * `truncate` non taglia niente.
 */
const DetailRow: Component<{ label: string; children: JSX.Element }> = (
  props,
) => (
  <div class="flex items-center justify-between gap-4">
    <span class="shrink-0 text-muted-foreground text-sm">{props.label}</span>
    <div class="min-w-0 flex-1 text-right">{props.children}</div>
  </div>
);

/**
 * «Chi siamo» — la narrativa dell'azienda, aperta di default.
 *
 * La descrizione è **integrale**: questa sheet è già il riassunto della vetrina,
 * e riassumere un riassunto vorrebbe dire mandare il candidato altrove per
 * leggere tre righe in più.
 */
export const AboutSection: Component<{ company: CompanyV2 }> = (props) => {
  const { t } = useI18n();

  const description = () => props.company.description?.trim() || undefined;
  const claim = () => props.company.company_culture_claim?.trim() || undefined;

  const hasContent = () => Boolean(description()) || Boolean(claim());

  return (
    <Show when={hasContent()}>
      <AccordionSection
        icon="ph-buildings"
        title={t("joblist.detailSheet.company.about.title")}
        defaultOpen
      >
        <Show when={description()}>
          {(text) => (
            <p class="whitespace-pre-line text-muted-foreground text-sm leading-relaxed">
              {text()}
            </p>
          )}
        </Show>
        {/* Il claim è una dichiarazione, non un'altra riga di descrizione: la
            barra e il corsivo lo staccano dal testo che lo precede, e con la
            sola descrizione assente resta comunque leggibile da solo. */}
        <Show when={claim()}>
          {(quote) => (
            <p
              class="mt-3 border-primary border-l-2 pl-3 font-medium text-sm italic"
              classList={{ "mt-0": !description() }}
            >
              “{quote()}”
            </p>
          )}
        </Show>
      </AccordionSection>
    </Show>
  );
};

/**
 * «Dettagli» — i fatti verificabili, aperti di default.
 *
 * Nessuna riga «Sede principale» (AC 10): la sede sta già nel blocco identità,
 * due righe più su, ed è lo stesso campo — non un secondo fatto. Il prototipo ce
 * l'aveva, ed è esattamente la duplicazione che la spec ha rimosso.
 */
export const DetailsSection: Component<{ company: CompanyV2 }> = (props) => {
  const { t } = useI18n();

  const size = () => props.company.company_size?.trim() || undefined;
  const foundedYear = () => props.company.founded_year?.trim() || undefined;
  const policies = () => parseRemotePolicies(props.company.remote_work_policy);
  /**
   * «sito.it» non è un URL renderizzabile: un `href` relativo navigherebbe
   * dentro l'app, su `/company/:id/sito.it`. Si scarta, non si ripara
   * prefissandogli uno schema che nessuno ha dichiarato (EP5).
   */
  const website = () => {
    const raw = props.company.website;
    return isRenderableUrl(raw) ? raw : null;
  };

  /**
   * Il gate legge **le stesse quattro condizioni** che decidono le righe: se
   * guardasse una lista di campi diversa — `remote_work_policy` grezza invece
   * delle policy superstiti, `website` grezzo invece di quello renderizzabile —
   * esisterebbero company che aprono una sezione vuota, che è il difetto che AC
   * 6 vieta (EC5).
   */
  const hasAnyRow = () =>
    Boolean(size()) ||
    Boolean(foundedYear()) ||
    policies().length > 0 ||
    Boolean(website());

  /** «51-200 · dal 2015», con le parti mancanti omesse invece che sostituite. */
  const hint = () => {
    const parts: string[] = [];
    const currentSize = size();
    if (currentSize) parts.push(currentSize);
    const year = foundedYear();
    if (year) {
      parts.push(
        t("joblist.detailSheet.company.details.hintFoundedYear", { year }),
      );
    }
    return parts.length > 0 ? parts.join(" · ") : undefined;
  };

  return (
    <Show when={hasAnyRow()}>
      <AccordionSection
        icon="ph-info"
        title={t("joblist.detailSheet.company.details.title")}
        hint={hint()}
        defaultOpen
      >
        <div class="space-y-3">
          {/* Valore grezzo della colonna: `company_size` è un `text` senza
              vincolo, e rimapparlo su una scala di comodo direbbe una cosa che
              l'azienda non ha scritto. */}
          <Show when={size()}>
            {(value) => (
              <DetailRow label={t("joblist.detailSheet.company.details.size")}>
                <span class="block truncate font-medium text-sm">
                  {value()}
                </span>
              </DetailRow>
            )}
          </Show>
          <Show when={foundedYear()}>
            {(year) => (
              <DetailRow
                label={t("joblist.detailSheet.company.details.foundedYear")}
              >
                <span class="block truncate font-medium text-sm">{year()}</span>
              </DetailRow>
            )}
          </Show>
          {/* I token che nessuno sa rendere sono già stati scartati a monte: qui
              non può arrivare né un badge vuoto né il testo grezzo della
              colonna, e con zero policy la riga non esiste affatto (EC5). Le
              etichette sono quelle della vetrina — la stessa policy non può
              chiamarsi in due modi in due schermate. */}
          <Show when={policies().length > 0}>
            <DetailRow
              label={t("joblist.detailSheet.company.details.remotePolicy")}
            >
              <div class="flex flex-wrap justify-end gap-1.5">
                <For each={policies()}>
                  {(policy) => (
                    <Badge variant="secondary" size="sm">
                      {t(`company.publicPage.remotePolicy.${policy}` as const)}
                    </Badge>
                  )}
                </For>
              </div>
            </DetailRow>
          </Show>
          <Show when={website()}>
            {(url) => (
              <DetailRow
                label={t("joblist.detailSheet.company.details.website")}
              >
                <a
                  href={url()}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block truncate font-medium text-primary text-sm hover:underline"
                >
                  {urlLabel(url())}
                </a>
              </DetailRow>
            )}
          </Show>
        </div>
      </AccordionSection>
    </Show>
  );
};

/**
 * «Benefit e cultura» — chiusa all'apertura della sheet.
 *
 * La forma dei benefit la decide `benefitsView`, non questa sezione: una frase
 * con le virgole diventerebbe quattro chip e un hint «4 benefit» che non
 * descrive niente (EC8). Il corollario è l'hint stesso — contare ha senso solo
 * quando ci sono cose da contare, quindi nella forma a paragrafo sparisce.
 */
export const BenefitsSection: Component<{ company: CompanyV2 }> = (props) => {
  const { t } = useI18n();

  const view = createMemo(() => benefitsView(props.company.benefits_and_perks));
  /* Il restringimento passa da una const locale: su una chiamata di funzione
     TypeScript non propaga il narrowing, e il ramo perderebbe il suo tipo. */
  const chips = () => {
    const current = view();
    return current?.kind === "chips" ? current : undefined;
  };
  const prose = () => {
    const current = view();
    return current?.kind === "paragraph" ? current : undefined;
  };

  const culture = () => props.company.company_culture?.trim() || undefined;

  const hasContent = () => Boolean(culture()) || view() !== null;

  const hint = () => {
    const items = chips()?.items;
    return items
      ? t("joblist.detailSheet.company.benefits.hint", { count: items.length })
      : undefined;
  };

  return (
    <Show when={hasContent()}>
      <AccordionSection
        icon="ph-heart"
        title={t("joblist.detailSheet.company.benefits.title")}
        hint={hint()}
      >
        <Show when={culture()}>
          {(text) => (
            <p class="mb-3 whitespace-pre-line text-muted-foreground text-sm leading-relaxed">
              {text()}
            </p>
          )}
        </Show>
        <Show when={chips()}>
          {(view) => (
            <div class="flex flex-wrap gap-2">
              <For each={view().items}>
                {(benefit) => (
                  /* `outline` + `bg-card` come sulla vetrina: le stesse chip,
                     nello stesso modo, sullo stesso fondo. */
                  <Badge variant="outline" size="md" class="gap-1.5 bg-card">
                    <i
                      class={`ph ${benefit.icon} text-primary`}
                      aria-hidden="true"
                    />
                    {benefit.label}
                  </Badge>
                )}
              </For>
            </div>
          )}
        </Show>
        <Show when={prose()}>
          {(view) => (
            <p class="whitespace-pre-line text-muted-foreground text-sm leading-relaxed">
              {view().text}
            </p>
          )}
        </Show>
      </AccordionSection>
    </Show>
  );
};
